import QRCode from 'qrcode';
import type { InvisibleCitiesEngine, EngineTelemetry } from '../core/engine';
import type { IsidoraSoundscape } from '../audio/soundscape';
import type {
  InteractiveElement,
  Locale,
  MotifId,
  NarrativePOI,
} from '../core/types';

export interface HudOptions {
  initialInspectorOpen?: boolean;
  initialLocale?: Locale;
}

const UI_STRINGS = {
  zh: {
    bowStrings: '♩ 撥奏琴弦',
    bowTitle: '撥奏伊西多拉製琴拱廊之泛音和弦',
    soundOn: '◈ 音效：開',
    soundOff: '◇ 音效：關',
    soundTitle: '切換程序化 WebAudio 環境音景 (M)',
    inspector: '⚙ 造物檢視器',
    inspectorTitle: '開啟程序化幾何標本與架構檢視器 (I)',
    qrBtn: '📱 手機掃描',
    qrTitle: '顯示 QR Code 供手機掃描開啟 (Q)',
    langBtn: 'EN / 中文',
    langTitle: '切換中／英文介面 (L)',
    hideHud: '純享視野',
    hideHudTitle: '隱藏／顯示介面面板 (H)',
    inspectionMode: '檢視模式',
    modeCity: '城市全景模型',
    modeSpecimen: '獨立標本旋轉台',
    specimenTitle: '程序化幾何標本（旋轉台）',
    formulaTitle: '數學生成公式',
    passesTitle: '渲染與材質通道',
    wireframe: '線框拓撲',
    bloom: '輝光後處理',
    autoOrbit: '自動環繞',
    telemetryTitle: '即時場景遙測數據',
    desirePole: '慾望 · 青年之夢',
    memoryPole: '記憶 · 老者之牆',
    specimenBadge: '幾何標本',
    specimenReflection:
      '獨立標本旋轉台模式——拖曳滑鼠可近距離環繞檢視，或於右側檢視器開啟「線框拓撲（Wireframe）」觀察程序化網格結構。',
    qrModalTitle: '手機掃描開啟 · 伊西多拉',
    qrModalDesc:
      '使用手機相機掃描上方 QR Code，即可在手機上以觸控旋轉視角、撥動琴弦並體驗暮色與記憶的轉換。',
    copyLink: '複製分享連結',
    copiedLink: '✓ 已複製連結',
    motifChips: {
      spirals: '螺旋階梯與海螺',
      telescopes: '完美望遠鏡',
      violins: '克雷莫納小提琴',
      crossroads: '猶豫的異鄉人與鬥雞',
      wall: '慾望已經是記憶',
    } as Record<string, string>,
    telemetryKeys: {
      mode: '檢視模式',
      fps: '畫面幀率 (FPS)',
      triangles: '三角面數',
      vertices: '頂點總數',
      drawCalls: '繪製呼叫',
      temporal: '時間態 (0→1)',
      spiralTowers: '螺旋階梯塔',
      encrustedSeashells: '鑲嵌海螺數',
      violinsAndStrings: '提琴與共鳴弦',
      telescopes: '黃銅望遠鏡',
    },
  },
  en: {
    bowStrings: '♩ Bow Strings',
    bowTitle: "Bow a resonant D-Dorian chord on Isidora's luthier strings",
    soundOn: '◈ Sound On',
    soundOff: '◇ Sound Off',
    soundTitle: 'Toggle generative WebAudio soundscape (M)',
    inspector: '⚙ Atelier Inspector',
    inspectorTitle: 'Open Specimen & Architecture Inspector (I)',
    qrBtn: '📱 QR Code',
    qrTitle: 'Show QR Code to open on mobile phone (Q)',
    langBtn: '中 / EN',
    langTitle: 'Switch between Traditional Chinese & English (L)',
    hideHud: 'Cinema View',
    hideHudTitle: 'Toggle clean cinema view (H)',
    inspectionMode: 'Inspection Mode',
    modeCity: 'City Diorama',
    modeSpecimen: 'Specimen Turntable',
    specimenTitle: 'Procedural Specimens (Turntable)',
    formulaTitle: 'Mathematical Generator Formula',
    passesTitle: 'Render & Shader Passes',
    wireframe: 'Wireframe',
    bloom: 'Bloom Pass',
    autoOrbit: 'Auto-Orbit',
    telemetryTitle: 'Live Scene Graph Telemetry',
    desirePole: 'Desire · Youth',
    memoryPole: 'Memory · Wall',
    specimenBadge: 'SPECIMEN',
    specimenReflection:
      'Isolated turntable study — drag to orbit closely, or toggle Wireframe in the Inspector to examine the parametric mesh topology.',
    qrModalTitle: 'Open on Mobile · Isidora',
    qrModalDesc:
      'Scan with your phone camera to explore the 3D spiral citadel, pluck the violin strings, and shift between Desire and Memory.',
    copyLink: 'Copy Share Link',
    copiedLink: '✓ Link Copied',
    motifChips: {
      spirals: 'spiral staircases & seashells',
      telescopes: 'perfect telescopes',
      violins: 'violins',
      crossroads: 'hesitating foreigner & brawls',
      wall: 'desires are already memories',
    } as Record<string, string>,
    telemetryKeys: {
      mode: 'Mode',
      fps: 'FPS',
      triangles: 'Triangles',
      vertices: 'Vertices',
      drawCalls: 'Draw Calls',
      temporal: 'Temporal (0→1)',
      spiralTowers: 'Spiral Towers',
      encrustedSeashells: 'Nacre Shells',
      violinsAndStrings: 'Violin Strings',
      telescopes: 'Telescopes',
    },
  },
};

export class IsidoraHUD {
  private root: HTMLElement;
  private engine: InvisibleCitiesEngine;
  private soundscape: IsidoraSoundscape;
  private locale: Locale;

  private mastheadKickerEl!: HTMLElement;
  private mastheadTitleEl!: HTMLElement;
  private inspectorDrawer!: HTMLElement;
  private poiRomanEl!: HTMLElement;
  private poiMotifTagEl!: HTMLElement;
  private poiTitleEl!: HTMLElement;
  private poiItalianEl!: HTMLElement;
  private poiQuoteEl!: HTMLElement;
  private poiReflectionEl!: HTMLElement;
  private temporalSlider!: HTMLInputElement;
  private poleDesireEl!: HTMLElement;
  private poleMemoryEl!: HTMLElement;

  private tooltipEl!: HTMLElement;
  private tooltipTitleEl!: HTMLElement;
  private tooltipDetailEl!: HTMLElement;

  private formulaBoxEl!: HTMLElement;
  private telemetryContainerEl!: HTMLElement;

  private qrModalBackdrop!: HTMLElement;
  private qrModalTitleEl!: HTMLElement;
  private qrModalDescEl!: HTMLElement;
  private qrSvgBoxEl!: HTMLElement;
  private qrUrlLabelEl!: HTMLElement;
  private qrCopyBtn!: HTMLButtonElement;

  private poiButtons = new Map<MotifId, HTMLButtonElement>();
  private motifChips = new Map<MotifId, HTMLButtonElement>();
  private specimenButtons = new Map<string, HTMLButtonElement>();
  private modeTabCity!: HTMLButtonElement;
  private modeTabSpecimen!: HTMLButtonElement;

  private pluckBtn!: HTMLButtonElement;
  private soundBtn!: HTMLButtonElement;
  private inspectorBtn!: HTMLButtonElement;
  private qrBtn!: HTMLButtonElement;
  private langBtn!: HTMLButtonElement;
  private hideHudBtn!: HTMLButtonElement;
  private wireBtn!: HTMLButtonElement;
  private bloomBtn!: HTMLButtonElement;
  private orbitBtn!: HTMLButtonElement;

  private inspectorOpen = false;
  private qrModalOpen = false;
  private hudHidden = false;

  constructor(
    root: HTMLElement,
    engine: InvisibleCitiesEngine,
    soundscape: IsidoraSoundscape,
    opts: HudOptions = {}
  ) {
    this.root = root;
    this.engine = engine;
    this.soundscape = soundscape;
    this.inspectorOpen = opts.initialInspectorOpen ?? false;
    this.locale = opts.initialLocale ?? 'zh';

    this.render();
    this.applyLocaleStrings();
    this.syncActivePOI(this.engine.getActivePOI());
    this.updateTelemetry(this.engine.getTelemetry());
  }

  public getLocale(): Locale {
    return this.locale;
  }

  public setLocale(locale: Locale): void {
    this.locale = locale;
    document.documentElement.lang = locale === 'zh' ? 'zh-Hant' : 'en';
    this.applyLocaleStrings();
    this.syncActivePOI(this.engine.getActivePOI());
    this.updateTelemetry(this.engine.getTelemetry());
  }

  public toggleLocale(): Locale {
    const next: Locale = this.locale === 'zh' ? 'en' : 'zh';
    this.setLocale(next);
    return next;
  }

  private render(): void {
    const city = this.engine.getActiveCity();

    this.root.innerHTML = `
      <header class="top-bar">
        <div class="masthead hud-fadeable">
          <span class="masthead-kicker" id="masthead-kicker"></span>
          <div class="masthead-title-row">
            <h1 class="masthead-title" id="masthead-title"></h1>
            <span class="masthead-subtitle">${city.italianName}</span>
          </div>
        </div>

        <div class="top-actions">
          <button class="pill-btn hud-fadeable" id="btn-pluck-chord">
            <span id="pluck-label"></span>
          </button>
          <button class="pill-btn hud-fadeable" id="btn-sound">
            <span id="sound-label"></span>
            <span class="kbd-hint">M</span>
          </button>
          <button class="pill-btn hud-fadeable ${this.inspectorOpen ? 'active' : ''}" id="btn-inspector">
            <span id="inspector-label"></span>
            <span class="kbd-hint">I</span>
          </button>
          <button class="pill-btn hud-fadeable" id="btn-qr">
            <span id="qr-label"></span>
            <span class="kbd-hint">Q</span>
          </button>
          <button class="pill-btn hud-fadeable" id="btn-lang">
            <span id="lang-label"></span>
            <span class="kbd-hint">L</span>
          </button>
          <button class="pill-btn" id="btn-hide-hud">
            <span id="hide-hud-label"></span>
            <span class="kbd-hint">H</span>
          </button>
        </div>
      </header>

      <div class="middle-stage">
        <section class="narrative-card hud-fadeable" aria-live="polite">
          <div class="poi-header">
            <span class="poi-roman" id="poi-roman">I</span>
            <span class="poi-motif-tag" id="poi-motif-tag"></span>
          </div>
          <h2 class="poi-title" id="poi-title"></h2>
          <div class="poi-italian" id="poi-italian"></div>
          <blockquote class="poi-quote" id="poi-quote"></blockquote>
          <p class="poi-reflection" id="poi-reflection"></p>

          <div class="motif-prose-bar" id="motif-chips">
            <button class="motif-chip" data-motif="spirals"></button>
            <button class="motif-chip" data-motif="telescopes"></button>
            <button class="motif-chip" data-motif="violins"></button>
            <button class="motif-chip" data-motif="crossroads"></button>
            <button class="motif-chip" data-motif="wall"></button>
          </div>
        </section>

        <aside class="inspector-drawer hud-fadeable ${this.inspectorOpen ? '' : 'closed'}" id="inspector-drawer">
          <div>
            <div class="inspector-section-title" id="sec-mode-title"></div>
            <div class="mode-tabs">
              <button class="mode-tab active" id="tab-mode-city"></button>
              <button class="mode-tab" id="tab-mode-specimen"></button>
            </div>
          </div>

          <div id="specimen-selector-section">
            <div class="inspector-section-title" id="sec-specimen-title"></div>
            <div class="specimen-list" id="specimen-list"></div>
          </div>

          <div>
            <div class="inspector-section-title" id="sec-formula-title"></div>
            <div class="formula-box" id="formula-box">r(θ) = r₀ · e^(bθ) · Logarithmic Citadel</div>
          </div>

          <div>
            <div class="inspector-section-title" id="sec-passes-title"></div>
            <div class="toggle-row">
              <button class="pill-btn" id="btn-toggle-wireframe"></button>
              <button class="pill-btn active" id="btn-toggle-bloom"></button>
              <button class="pill-btn active" id="btn-toggle-orbit"></button>
            </div>
          </div>

          <div>
            <div class="inspector-section-title" id="sec-telemetry-title"></div>
            <div class="telemetry-grid" id="telemetry-grid"></div>
          </div>
        </aside>
      </div>

      <footer class="bottom-bar hud-fadeable">
        <nav class="poi-stepper" id="poi-stepper" aria-label="Narrative Points of Interest"></nav>

        <div class="temporal-control">
          <span class="temporal-pole desire" id="pole-desire"></span>
          <input
            type="range"
            min="0"
            max="1"
            step="0.01"
            value="0.15"
            class="temporal-slider"
            id="temporal-slider"
            aria-label="Temporal transition from Youth's Desire to Old Age's Memory"
          />
          <span class="temporal-pole memory" id="pole-memory"></span>
        </div>
      </footer>

      <!-- QR Code Modal -->
      <div class="qr-modal-backdrop closed" id="qr-modal-backdrop" role="dialog" aria-modal="true">
        <div class="qr-modal-card">
          <div class="qr-modal-header">
            <span class="qr-modal-title" id="qr-modal-title"></span>
            <button class="qr-close-btn" id="qr-close-btn" aria-label="Close">×</button>
          </div>
          <div class="qr-svg-box" id="qr-svg-box"></div>
          <div class="qr-url-label" id="qr-url-label">https://cities.896505.xyz</div>
          <p class="qr-modal-desc" id="qr-modal-desc"></p>
          <button class="pill-btn active" id="qr-copy-btn"></button>
        </div>
      </div>

      <div class="hover-tooltip" id="hover-tooltip">
        <div class="tooltip-title" id="tooltip-title"></div>
        <div class="tooltip-detail" id="tooltip-detail"></div>
      </div>
    `;

    this.mastheadKickerEl = this.root.querySelector('#masthead-kicker') as HTMLElement;
    this.mastheadTitleEl = this.root.querySelector('#masthead-title') as HTMLElement;
    this.inspectorDrawer = this.root.querySelector('#inspector-drawer') as HTMLElement;
    this.poiRomanEl = this.root.querySelector('#poi-roman') as HTMLElement;
    this.poiMotifTagEl = this.root.querySelector('#poi-motif-tag') as HTMLElement;
    this.poiTitleEl = this.root.querySelector('#poi-title') as HTMLElement;
    this.poiItalianEl = this.root.querySelector('#poi-italian') as HTMLElement;
    this.poiQuoteEl = this.root.querySelector('#poi-quote') as HTMLElement;
    this.poiReflectionEl = this.root.querySelector('#poi-reflection') as HTMLElement;
    this.temporalSlider = this.root.querySelector('#temporal-slider') as HTMLInputElement;
    this.poleDesireEl = this.root.querySelector('#pole-desire') as HTMLElement;
    this.poleMemoryEl = this.root.querySelector('#pole-memory') as HTMLElement;

    this.tooltipEl = this.root.querySelector('#hover-tooltip') as HTMLElement;
    this.tooltipTitleEl = this.root.querySelector('#tooltip-title') as HTMLElement;
    this.tooltipDetailEl = this.root.querySelector('#tooltip-detail') as HTMLElement;

    this.formulaBoxEl = this.root.querySelector('#formula-box') as HTMLElement;
    this.telemetryContainerEl = this.root.querySelector('#telemetry-grid') as HTMLElement;

    this.qrModalBackdrop = this.root.querySelector('#qr-modal-backdrop') as HTMLElement;
    this.qrModalTitleEl = this.root.querySelector('#qr-modal-title') as HTMLElement;
    this.qrModalDescEl = this.root.querySelector('#qr-modal-desc') as HTMLElement;
    this.qrSvgBoxEl = this.root.querySelector('#qr-svg-box') as HTMLElement;
    this.qrUrlLabelEl = this.root.querySelector('#qr-url-label') as HTMLElement;
    this.qrCopyBtn = this.root.querySelector('#qr-copy-btn') as HTMLButtonElement;

    this.modeTabCity = this.root.querySelector('#tab-mode-city') as HTMLButtonElement;
    this.modeTabSpecimen = this.root.querySelector('#tab-mode-specimen') as HTMLButtonElement;
    this.pluckBtn = this.root.querySelector('#btn-pluck-chord') as HTMLButtonElement;
    this.soundBtn = this.root.querySelector('#btn-sound') as HTMLButtonElement;
    this.inspectorBtn = this.root.querySelector('#btn-inspector') as HTMLButtonElement;
    this.qrBtn = this.root.querySelector('#btn-qr') as HTMLButtonElement;
    this.langBtn = this.root.querySelector('#btn-lang') as HTMLButtonElement;
    this.hideHudBtn = this.root.querySelector('#btn-hide-hud') as HTMLButtonElement;
    this.wireBtn = this.root.querySelector('#btn-toggle-wireframe') as HTMLButtonElement;
    this.bloomBtn = this.root.querySelector('#btn-toggle-bloom') as HTMLButtonElement;
    this.orbitBtn = this.root.querySelector('#btn-toggle-orbit') as HTMLButtonElement;

    // Populate POI stepper buttons
    const stepperEl = this.root.querySelector('#poi-stepper') as HTMLElement;
    for (const poi of city.pois) {
      const btn = document.createElement('button');
      btn.className = 'poi-step-btn';
      btn.addEventListener('click', () => {
        this.engine.selectPOI(poi.id, true);
        this.soundscape.playMotifCue(poi.id);
      });
      stepperEl.appendChild(btn);
      this.poiButtons.set(poi.id, btn);
    }

    // Bind Motif prose chips
    this.root.querySelectorAll<HTMLButtonElement>('.motif-chip').forEach((chip) => {
      const motif = chip.dataset.motif as MotifId;
      this.motifChips.set(motif, chip);
      chip.addEventListener('click', () => {
        this.engine.selectPOI(motif, true);
        this.soundscape.playMotifCue(motif);
      });
    });

    // Populate Specimen Turntable buttons
    const specListEl = this.root.querySelector('#specimen-list') as HTMLElement;
    for (const spec of city.specimens) {
      const btn = document.createElement('button');
      btn.className = 'specimen-btn';
      btn.addEventListener('click', () => {
        this.selectSpecimen(spec.id);
      });
      specListEl.appendChild(btn);
      this.specimenButtons.set(spec.id, btn);
    }

    this.bindControls();
  }

  private applyLocaleStrings(): void {
    const s = UI_STRINGS[this.locale];
    const city = this.engine.getActiveCity();

    this.mastheadKickerEl.textContent =
      this.locale === 'zh'
        ? city.zhCategory
        : `Italo Calvino · ${city.category} · ${city.categoryIndex}`;
    this.mastheadTitleEl.textContent =
      this.locale === 'zh' ? city.zhName : city.name.toUpperCase();

    const pluckLabel = this.pluckBtn.querySelector('#pluck-label');
    if (pluckLabel) pluckLabel.textContent = s.bowStrings;
    this.pluckBtn.title = s.bowTitle;

    const soundLabel = this.soundBtn.querySelector('#sound-label');
    if (soundLabel) {
      soundLabel.textContent = this.soundscape.isEnabled() ? s.soundOn : s.soundOff;
    }
    this.soundBtn.title = s.soundTitle;

    const inspectorLabel = this.inspectorBtn.querySelector('#inspector-label');
    if (inspectorLabel) inspectorLabel.textContent = s.inspector;
    this.inspectorBtn.title = s.inspectorTitle;

    const qrLabel = this.qrBtn.querySelector('#qr-label');
    if (qrLabel) qrLabel.textContent = s.qrBtn;
    this.qrBtn.title = s.qrTitle;

    const langLabel = this.langBtn.querySelector('#lang-label');
    if (langLabel) langLabel.textContent = s.langBtn;
    this.langBtn.title = s.langTitle;

    const hideLabel = this.hideHudBtn.querySelector('#hide-hud-label');
    if (hideLabel) hideLabel.textContent = s.hideHud;
    this.hideHudBtn.title = s.hideHudTitle;

    (this.root.querySelector('#sec-mode-title') as HTMLElement).textContent = s.inspectionMode;
    this.modeTabCity.textContent = s.modeCity;
    this.modeTabSpecimen.textContent = s.modeSpecimen;
    (this.root.querySelector('#sec-specimen-title') as HTMLElement).textContent = s.specimenTitle;
    (this.root.querySelector('#sec-formula-title') as HTMLElement).textContent = s.formulaTitle;
    (this.root.querySelector('#sec-passes-title') as HTMLElement).textContent = s.passesTitle;
    this.wireBtn.textContent = s.wireframe;
    this.bloomBtn.textContent = s.bloom;
    this.orbitBtn.textContent = s.autoOrbit;
    (this.root.querySelector('#sec-telemetry-title') as HTMLElement).textContent = s.telemetryTitle;

    this.poleDesireEl.textContent = s.desirePole;
    this.poleMemoryEl.textContent = s.memoryPole;

    this.qrModalTitleEl.textContent = s.qrModalTitle;
    this.qrModalDescEl.textContent = s.qrModalDesc;
    this.qrCopyBtn.textContent = s.copyLink;

    // Update Motif chips
    this.motifChips.forEach((chip, motif) => {
      chip.textContent = s.motifChips[motif] ?? motif;
    });

    // Update POI stepper labels
    for (const poi of city.pois) {
      const btn = this.poiButtons.get(poi.id);
      if (btn) {
        const label =
          this.locale === 'zh'
            ? poi.zh.shortTabLabel
            : poi.motifLabel.split('&')[0].trim();
        btn.innerHTML = `<span>${poi.number}</span><span>${label}</span>`;
      }
    }

    // Update Specimen buttons
    for (const spec of city.specimens) {
      const btn = this.specimenButtons.get(spec.id);
      if (btn) {
        btn.textContent =
          this.locale === 'zh' ? spec.zh.shortName : spec.name.split('(')[0].trim();
      }
    }
  }

  private bindControls(): void {
    this.soundBtn.addEventListener('click', () => this.toggleSound());

    this.pluckBtn.addEventListener('click', () => {
      if (!this.soundscape.isEnabled()) {
        this.toggleSound();
      }
      this.engine.triggerMotif('violins');
      [196.0, 293.66, 440.0, 659.25].forEach((freq, idx) => {
        setTimeout(() => this.soundscape.playViolinString(freq), idx * 110);
      });
    });

    this.inspectorBtn.addEventListener('click', () => this.toggleInspector());
    this.qrBtn.addEventListener('click', () => this.toggleQrModal());
    this.langBtn.addEventListener('click', () => this.toggleLocale());
    this.hideHudBtn.addEventListener('click', () => this.toggleHudVisibility());

    // QR Modal controls
    const qrCloseBtn = this.root.querySelector('#qr-close-btn') as HTMLButtonElement;
    qrCloseBtn.addEventListener('click', () => this.toggleQrModal(false));
    this.qrModalBackdrop.addEventListener('click', (e) => {
      if (e.target === this.qrModalBackdrop) {
        this.toggleQrModal(false);
      }
    });

    this.qrCopyBtn.addEventListener('click', () => {
      const url = this.qrUrlLabelEl.textContent || 'https://cities.896505.xyz';
      navigator.clipboard?.writeText(url);
      const s = UI_STRINGS[this.locale];
      this.qrCopyBtn.textContent = s.copiedLink;
      setTimeout(() => {
        this.qrCopyBtn.textContent = UI_STRINGS[this.locale].copyLink;
      }, 1800);
    });

    this.modeTabCity.addEventListener('click', () => {
      this.engine.setMode('city');
      this.syncActivePOI(this.engine.getActivePOI());
      this.updateTelemetry(this.engine.getTelemetry());
    });

    this.modeTabSpecimen.addEventListener('click', () => {
      const firstSpec = this.engine.getActiveCity().specimens[0]?.id ?? 'seashell';
      this.selectSpecimen(firstSpec);
    });

    this.wireBtn.addEventListener('click', () => {
      const next = !this.wireBtn.classList.contains('active');
      this.wireBtn.classList.toggle('active', next);
      this.engine.setWireframe(next);
    });

    this.bloomBtn.addEventListener('click', () => {
      const next = !this.bloomBtn.classList.contains('active');
      this.bloomBtn.classList.toggle('active', next);
      this.engine.setBloomEnabled(next);
    });

    this.orbitBtn.addEventListener('click', () => {
      const next = !this.orbitBtn.classList.contains('active');
      this.orbitBtn.classList.toggle('active', next);
      this.engine.setAutoOrbit(next);
    });

    this.temporalSlider.addEventListener('input', () => {
      const val = parseFloat(this.temporalSlider.value);
      this.engine.setTemporalState(val);
      this.soundscape.setTemporalState(val);
      document.body.dataset.temporalMode = val > 0.52 ? 'memory' : 'desire';
    });

    this.poleDesireEl.addEventListener('click', () => {
      this.temporalSlider.value = '0.05';
      this.temporalSlider.dispatchEvent(new Event('input'));
    });
    this.poleMemoryEl.addEventListener('click', () => {
      this.temporalSlider.value = '0.95';
      this.temporalSlider.dispatchEvent(new Event('input'));
    });
  }

  public async toggleQrModal(forceState?: boolean): Promise<void> {
    this.qrModalOpen = forceState ?? !this.qrModalOpen;
    this.qrModalBackdrop.classList.toggle('closed', !this.qrModalOpen);
    this.qrBtn.classList.toggle('active', this.qrModalOpen);

    if (this.qrModalOpen) {
      const shareUrl = window.location.hostname.includes('896505.xyz')
        ? window.location.href
        : 'https://cities.896505.xyz/';
      this.qrUrlLabelEl.textContent = shareUrl;
      const svgString = await QRCode.toString(shareUrl, {
        type: 'svg',
        margin: 1,
        color: {
          dark: '#140f20',
          light: '#f8f2e8',
        },
      });
      this.qrSvgBoxEl.innerHTML = svgString;
    }
  }

  public selectSpecimen(specimenId: string): void {
    if (!this.inspectorOpen) {
      this.toggleInspector(true);
    }
    this.engine.setMode('specimen', specimenId);
    this.syncInspectorModeUI();
    this.updateTelemetry(this.engine.getTelemetry());
  }

  private syncInspectorModeUI(): void {
    const telemetry = this.engine.getTelemetry();
    const isSpecimen = telemetry.mode === 'specimen';
    const s = UI_STRINGS[this.locale];

    this.modeTabCity.classList.toggle('active', !isSpecimen);
    this.modeTabSpecimen.classList.toggle('active', isSpecimen);

    this.specimenButtons.forEach((btn, id) => {
      btn.classList.toggle('active', isSpecimen && telemetry.activeSpecimenId === id);
    });

    if (isSpecimen) {
      const specMeta = this.engine.getActiveSpecimenMeta();
      if (specMeta?.definition) {
        const def = specMeta.definition;
        this.formulaBoxEl.textContent = def.formula;
        this.poiRomanEl.textContent = s.specimenBadge;
        this.poiMotifTagEl.textContent =
          this.locale === 'zh' ? def.zh.category : def.category;
        this.poiTitleEl.textContent =
          this.locale === 'zh' ? def.zh.name : def.name;
        this.poiItalianEl.textContent = `Formula: ${def.formula}`;
        this.poiQuoteEl.textContent =
          this.locale === 'zh' ? def.zh.summary : def.summary;
        this.poiReflectionEl.textContent = s.specimenReflection;
      }
    } else {
      this.formulaBoxEl.textContent = 'r(θ) = r₀ · e^(bθ) · Logarithmic Spiral Citadel';
    }
  }

  public toggleSound(): void {
    const active = this.soundscape.toggle();
    this.soundBtn.classList.toggle('active', active);
    const s = UI_STRINGS[this.locale];
    const label = this.soundBtn.querySelector('#sound-label');
    if (label) {
      label.textContent = active ? s.soundOn : s.soundOff;
    }
    if (active) {
      this.soundscape.playSeashellResonance(293.66);
    }
  }

  public toggleInspector(forceState?: boolean): void {
    this.inspectorOpen = forceState ?? !this.inspectorOpen;
    this.inspectorDrawer.classList.toggle('closed', !this.inspectorOpen);
    this.inspectorBtn.classList.toggle('active', this.inspectorOpen);
  }

  public toggleHudVisibility(forceState?: boolean): void {
    this.hudHidden = forceState ?? !this.hudHidden;
    this.root.classList.toggle('hud-hidden', this.hudHidden);
    this.hideHudBtn.classList.toggle('active', this.hudHidden);
  }

  public syncActivePOI(poi: NarrativePOI): void {
    const isZh = this.locale === 'zh';
    this.poiRomanEl.textContent = isZh
      ? `第一章 · 視角 ${poi.number}`
      : `CHAPTER I · ${poi.number}`;
    this.poiMotifTagEl.textContent = isZh ? poi.zh.motifLabel : poi.motifLabel;
    this.poiTitleEl.textContent = isZh ? poi.zh.title : poi.title;
    this.poiItalianEl.textContent = poi.italianSubtitle;
    this.poiQuoteEl.textContent = isZh ? poi.zh.excerpt : poi.excerpt;
    this.poiReflectionEl.textContent = isZh ? poi.zh.reflection : poi.reflection;

    this.poiButtons.forEach((btn, id) => {
      btn.classList.toggle('active', id === poi.id);
    });
    this.motifChips.forEach((chip, id) => {
      chip.classList.toggle('active', id === poi.id);
    });

    if (typeof poi.suggestedTemporal === 'number') {
      this.temporalSlider.value = String(poi.suggestedTemporal);
      document.body.dataset.temporalMode =
        poi.suggestedTemporal > 0.52 ? 'memory' : 'desire';
    }
    this.syncInspectorModeUI();
  }

  public updateHoverTooltip(
    item: InteractiveElement | null,
    screenPos: { x: number; y: number }
  ): void {
    if (!item || this.hudHidden) {
      this.tooltipEl.classList.remove('visible');
      return;
    }
    const isZh = this.locale === 'zh';
    this.tooltipTitleEl.textContent = (isZh && item.zhTitle) ? item.zhTitle : item.title;
    this.tooltipDetailEl.textContent = (isZh && item.zhDetail) ? item.zhDetail : item.detail;
    this.tooltipEl.style.left = `${Math.min(window.innerWidth - 280, screenPos.x + 12)}px`;
    this.tooltipEl.style.top = `${Math.max(40, Math.min(window.innerHeight - 60, screenPos.y))}px`;
    this.tooltipEl.classList.add('visible');
  }

  public updateTelemetry(telemetry: EngineTelemetry): void {
    const cityStats = this.engine.getCityTelemetry();
    const k = UI_STRINGS[this.locale].telemetryKeys;
    const rows: Array<[string, string | number]> = [
      [k.mode, telemetry.mode.toUpperCase()],
      [k.fps, telemetry.fps],
      [k.triangles, telemetry.triangles.toLocaleString()],
      [k.vertices, telemetry.vertices.toLocaleString()],
      [k.drawCalls, telemetry.drawCalls],
      [k.temporal, telemetry.temporal.toFixed(2)],
      [k.spiralTowers, cityStats.spiralTowers],
      [k.encrustedSeashells, cityStats.encrustedSeashells],
      [k.violinsAndStrings, cityStats.violinsAndStrings],
      [k.telescopes, cityStats.telescopes],
    ];

    this.telemetryContainerEl.innerHTML = rows
      .map(
        ([label, v]) =>
          `<div class="telemetry-row"><span class="telemetry-key">${label}</span><span class="telemetry-val">${v}</span></div>`
      )
      .join('');
  }
}
